import { useState } from "react";
import { ToggleButton } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import styled from "styled-components";

import { Attempt } from "../hooks/useAttempts";
import { MainButton, MainButtons } from "./button";

type SportAttemptsInputProps = {
  attempt?: Attempt;
  onChange: (attempt: Attempt) => void;
  isActive?: boolean;
  disabled?: boolean;
  maxSegmentsCount: number;
};

const BottomButtons = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 70px;
`;

export const HoldNumberDisplay = ({
  number,
  maxNumber,
  disabled,
  usedLastHold,
  success,
}: {
  // onPlus: () => void;
  // onMinus: () => void;
  number: number;
  maxNumber: number;
  disabled?: boolean;
  usedLastHold?: boolean;
  success?: boolean;
}) => {
  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center"
      style={{ height: "calc(50vh - 150px)" }}
    >
      <span className="text-muted small">Hold number</span>
      <div className="text-center d-flex align-items-baseline">
        <div
          style={{
            fontSize: 80,
            lineHeight: 1,
            fontWeight: "bold",
            marginBottom: -10,
            color: success ? "#45D27E" : usedLastHold ? "#0075FF" : "#010024",
          }}
        >
          {number}
          {usedLastHold ? "+" : ""}
        </div>
        <span
          style={{ marginTop: -10, display: "inline-block", opacity: 0.7 }}
          className="text-muted"
        >
          /{maxNumber}
        </span>
      </div>
    </div>
  );
};

const AttemptsInputSport = ({
  attempt: initialAttempt,
  onChange,
  maxSegmentsCount,
  disabled,
}: SportAttemptsInputProps) => {
  const attempt =
    initialAttempt ||
    ({ segmentsCount: 0, usedLastHold: false, success: false } as Omit<
      Attempt,
      "timestamp"
    > & { timestamp?: Date });
  const [lastSegmentsValue, setLastSegmentsValue] = useState(
    attempt.segmentsCount
  );
  const [lastUsedLastHoldValue, setLastUsedLastHoldValue] = useState(
    attempt.usedLastHold
  );

  const onUpdateAttempt = (data: Partial<Attempt>) => {
    const update = {
      ...attempt,
      timestamp: attempt.timestamp || new Date(),
      ...data,
    };
    onChange(update);
  };

  const toggleTop = () => {
    if (attempt.success) {
      // restore previous values of last used hold / last segments value
      if (lastSegmentsValue === maxSegmentsCount) {
        onUpdateAttempt({
          success: false,
          segmentsCount: 0,
          usedLastHold: false,
        });
      } else {
        onUpdateAttempt({
          success: false,
          segmentsCount: lastSegmentsValue,
          usedLastHold: lastUsedLastHoldValue,
        });
      }
    } else {
      setLastSegmentsValue(attempt.segmentsCount);
      setLastUsedLastHoldValue(attempt.usedLastHold);
      onUpdateAttempt({
        success: true,
        segmentsCount: maxSegmentsCount,
        usedLastHold: false,
      });
    }
  };

  const onPlus = () => {
    if (attempt.success) {
      return;
    }

    if (attempt.segmentsCount + 1 === maxSegmentsCount) {
      toggleTop();
    } else {
      onUpdateAttempt({
        usedLastHold: false,
        segmentsCount: attempt.segmentsCount + 1,
      });
    }
  };

  const onMinus = () => {
    if (attempt.success) {
      onUpdateAttempt({
        success: false,
        segmentsCount: maxSegmentsCount - 1,
        usedLastHold: false,
      });
    } else {
      // // if decreasing to zero, force usedLastHold to be false.
      // const usedLastHold =
      //   attempt.segmentsCount === 1 ? false : attempt.usedLastHold;
      onUpdateAttempt({
        segmentsCount: Math.max(0, attempt.segmentsCount - 1),
        usedLastHold: false,
      });
    }
  };

  const [notesExpanded, setNotesExpanded] = useState(
    (attempt.notes?.length || 0) > 0
  );

  const setDuration = (duration: number) => onUpdateAttempt({ duration });
  const setNotes = (notes: string) => onUpdateAttempt({ notes });

  const usedLastHoldDisabled = attempt.success || attempt.segmentsCount === 0;

  const onUsedLastHoldChange = (usedLastHold: boolean) =>
    onUpdateAttempt({ usedLastHold });

  return (
    <div className="flex-fill d-flex flex-column">
      <HoldNumberDisplay
        number={attempt.segmentsCount}
        maxNumber={maxSegmentsCount}
        disabled={disabled}
        usedLastHold={attempt.usedLastHold}
        success={attempt.success}
      />
      <div className="row">
        {/*<Form.Group className="mt-3 d-flex">
          <Form.Label column>Time</Form.Label>
          <div className="flex-fill">
            <Form.Control
              placeholder="00:00"
              defaultValue={
                attempt.duration ? formatDuration(attempt.duration) : undefined
              }
              onChange={(e) => setDuration(parseDuration(e.target.value))}
            />
          </div>
            </Form.Group>*/}
        {!notesExpanded ? (
          <div
            className="text-muted"
            onClick={() => setNotesExpanded(!notesExpanded)}
          >
            + Note
          </div>
        ) : null}
        {notesExpanded ? (
          <Form.Group className="mt-3">
            <Form.Control
              placeholder="Add notes here..."
              as="textarea"
              value={attempt.notes || undefined}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        ) : null}
      </div>
      <BottomButtons>
        <div className="row">
          <div className="col justify-content-center align-items-center">
            <ToggleButton
              active={false}
              disabled={disabled || usedLastHoldDisabled}
              type="checkbox"
              variant="outline-primary"
              value="test"
              checked={attempt.usedLastHold}
              className="d-block"
              onClick={() => onUsedLastHoldChange(!attempt.usedLastHold)}
            >
              Used last hold
            </ToggleButton>
          </div>
          <div className="col justify-content-center align-items-center">
            <ToggleButton
              active={false}
              onClick={toggleTop}
              type="checkbox"
              variant="outline-success"
              value="test"
              checked={attempt.success}
              disabled={disabled}
              className="d-block"
            >
              {attempt.success ? "✔︎" : null} Top
            </ToggleButton>
          </div>
        </div>
        <MainButtons className="mt-2">
          <MainButton
            onClick={onMinus}
            disabled={disabled || attempt.segmentsCount === 0}
            variant="secondary"
          >
            <svg
              width="13"
              height="4"
              viewBox="0 0 13 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.0620117 3.417L0.0620117 0.5H12.938V3.417H0.0620117Z"
                fill="white"
              />
            </svg>
          </MainButton>
          <MainButton
            onClick={onPlus}
            disabled={disabled || attempt.segmentsCount === maxSegmentsCount}
            variant="secondary"
          >
            <svg
              width="13"
              height="14"
              viewBox="0 0 13 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.04201 13.417V8.45801H0.0620117L0.0620117 5.54201H5.04201V0.562012H7.95801V5.54201H12.938V8.45801H7.95801V13.417H5.04201Z"
                fill="white"
              />
            </svg>
          </MainButton>
        </MainButtons>
      </BottomButtons>
    </div>
  );
};
export default AttemptsInputSport;
